import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
} from '@mui/material';
import apiService from '../../services/api';
import { PaymentStatistics } from '../../types';

const formatCurrency = (value: number) =>
  `₹${Math.round(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

const PaymentDashboardPage: React.FC = () => {
  const [paymentStats, setPaymentStats] = useState<PaymentStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const PAYMENTS_PER_PAGE = 5;

  const fetchPaymentStats = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const stats = await apiService.getPaymentStatistics({
        page,
        per_page: PAYMENTS_PER_PAGE,
      });
      setPaymentStats(stats);
    } catch (err: any) {
      console.error('Failed to load payment statistics:', err);
      setError(err.response?.data?.message || 'Failed to load payment statistics');
    } finally {
      setIsLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchPaymentStats();
  }, [fetchPaymentStats]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!paymentStats) {
    return null;
  }

  const cards = [
    { title: 'Current Month Revenue', value: paymentStats.current_month_revenue, color: '#1E88E5' },
    { title: 'Current Day Revenue', value: paymentStats.current_day_revenue, color: '#43A047' },
    { title: 'Current Month Transaction Success', value: paymentStats.current_month_success_count, color: '#00ACC1' },
    { title: 'Current Month Transaction Failure', value: paymentStats.current_month_failure_count, color: '#E53935' },
    { title: 'Current Day Transaction Success', value: paymentStats.current_day_success_count, color: '#8E24AA' },
    { title: 'Current Day Transaction Failure', value: paymentStats.current_day_failure_count, color: '#FB8C00' },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Payment Dashboard
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {cards.map((card) => (
          <Grid key={card.title} size={{ xs: 12, sm: 6, md: 2 }}>
            <Card sx={{ height: '100%', border: `1px solid ${card.color}33`, backgroundColor: `${card.color}14` }}>
              <CardContent>
                <Typography color="textSecondary" variant="body2" sx={{ fontWeight: 500 }}>
                  {card.title}
                </Typography>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: 1, color: card.color }}>
                  {card.title.toLowerCase().includes('revenue')
                    ? formatCurrency(card.value)
                    : card.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box mb={4}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
          Today's Payment Transactions
        </Typography>
        <Card>
          <CardContent>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Patient</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      Amount
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      Status
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">
                      Time
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(paymentStats.today_payments || []).map((payment) => (
                    <TableRow key={payment.id}>
                      <TableCell>{payment.payment_reference}</TableCell>
                      <TableCell>{payment.patient?.name || '—'}</TableCell>
                      <TableCell align="right">
                        {formatCurrency(payment.amount)}
                      </TableCell>
                      <TableCell align="right">{payment.status}</TableCell>
                      <TableCell align="right">{payment.created_at}</TableCell>
                    </TableRow>
                  ))}
                  {(paymentStats.today_payments?.length || 0) === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        No payments recorded today.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Pagination
                count={paymentStats.pagination.last_page}
                page={page}
                onChange={(_e, value) => setPage(value)}
                disabled={paymentStats.pagination.total <= PAYMENTS_PER_PAGE}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PaymentDashboardPage;

